import React, { useState } from 'react';
import styled from 'styled-components';
import { ScoringGuideModal } from './ScoringGuideModal';

const SliderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
`;

const SliderWrapper = styled.div`
  position: relative;
  width: 100%;
  height: var(--touch-target);
  display: flex;
  align-items: center;
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: 8px;
  background: linear-gradient(
    to right,
    #ef4444 0%,
    #f59e0b 25%,
    #eab308 50%,
    #84cc16 75%,
    #10b981 100%
  );
  border-radius: 4px;
`;

const SliderInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: var(--touch-target);
  opacity: 0;
  cursor: pointer;
  margin: 0;
  
  &::-webkit-slider-thumb {
    appearance: none;
    width: var(--touch-target);
    height: var(--touch-target);
    border-radius: 50%;
    background: var(--primary-color);
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  
  &::-moz-range-thumb {
    width: var(--touch-target);
    height: var(--touch-target);
    border-radius: 50%;
    background: var(--primary-color);
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const SliderThumb = styled.div<{ $position: number }>`
  position: absolute;
  top: 50%;
  left: ${props => props.$position}%;
  transform: translate(-50%, -50%);
  width: var(--touch-target);
  height: var(--touch-target);
  border-radius: 50%;
  background: var(--primary-color);
  border: 3px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.875rem;
`;

const PreviousThumb = styled.div<{ $position: number }>`
  position: absolute;
  top: 50%;
  left: ${props => props.$position}%;
  transform: translate(-50%, -50%);
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: var(--text-secondary);
  border: 2px solid white;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.75rem;
  opacity: 0.7;
`;

const ScaleLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: var(--spacing-xs);
  font-size: 0.75rem;
  color: var(--text-secondary);
`;

const ValueDisplay = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-xs);
`;

const InfoButton = styled.button`
  background: white;
  border: 2px solid #2563eb;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  color: #2563eb;
  font-size: 0.875rem;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: #f0f7ff;
    border-color: #1d4ed8;
    color: #1d4ed8;
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.2);
  }
  
  &:active {
    transform: scale(0.95);
    background: #e0f2fe;
  }
`;

const CurrentValue = styled.span`
  font-weight: 600;
  color: var(--text-primary);
  font-size: 1.125rem;
`;

const PreviousValue = styled.span`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const ChangeIndicator = styled.span<{ $change: number }>`
  font-size: 0.875rem;
  font-weight: 500;
  
  ${props => {
    if (props.$change > 0) {
      return `color: var(--success-color);`;
    } else if (props.$change < 0) {
      return `color: var(--error-color);`;
    } else {
      return `color: var(--text-secondary);`;
    }
  }}
`;

interface RatingSliderProps {
  value: number;
  onChange: (value: number) => void;
  previousValue?: number;
  min?: number;
  max?: number;
  skillKey?: string;
}

export const RatingSlider: React.FC<RatingSliderProps> = ({
  value,
  onChange,
  previousValue,
  min = 1,
  max = 10,
  skillKey,
}) => {
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(parseInt(event.target.value, 10));
  };

  const getPosition = (val: number) => {
    return ((val - min) / (max - min)) * 100;
  };

  const getChangeIndicator = () => {
    if (previousValue === undefined) return null;
    
    const change = value - previousValue;
    if (change > 0) {
      return `+${change}`;
    } else if (change < 0) {
      return `${change}`;
    } else {
      return '→';
    }
  };

  return (
    <SliderContainer>
      <ValueDisplay>
        <CurrentValue>Score: {value}</CurrentValue>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
          {skillKey && (
            <InfoButton 
              onClick={() => setIsGuideOpen(true)}
              title="View scoring guide for this skill"
              type="button"
            >
              ℹ
            </InfoButton>
          )}
          {previousValue !== undefined && (
            <div>
              <PreviousValue>Previous: {previousValue}</PreviousValue>
              <ChangeIndicator $change={value - previousValue}>
                {getChangeIndicator()}
              </ChangeIndicator>
            </div>
          )}
        </div>
      </ValueDisplay>
      
      <SliderWrapper>
        <SliderTrack />
        <SliderInput
          type="range"
          min={min}
          max={max}
          value={value}
          onChange={handleChange}
        />
        <SliderThumb $position={getPosition(value)}>
          {value}
        </SliderThumb>
        {previousValue !== undefined && (
          <PreviousThumb $position={getPosition(previousValue)}>
            {previousValue}
          </PreviousThumb>
        )}
      </SliderWrapper>
      
      <ScaleLabels>
        <span>Poor (1)</span>
        <span>Average (5)</span>
        <span>Excellent (10)</span>
      </ScaleLabels>
      
      {skillKey && (
        <ScoringGuideModal
          skillKey={skillKey}
          isOpen={isGuideOpen}
          onClose={() => setIsGuideOpen(false)}
        />
      )}
    </SliderContainer>
  );
};